// src/services/onboardingConfigs.js

/**
 * Onboarding configurations for different pages
 * Each configuration defines the steps for that page's tutorial
 */

const onboardingConfigs = {
  // Developer onboarding for the Projects/Dashboard page
  projectsDev: [
    {
      selector: '#assignments-section',
      title: 'Your Projects',
      message: 'This is where all your game projects are displayed. You can manage and track your projects here.',
      position: 'right'
    },
    {
      selector: '.new-project-inside',
      title: 'Create New Project',
      message: 'Click here to add a new game project to your dashboard.',
      position: 'left'
    },
    {
      selector: '.messages-container',
      title: 'Messages',
      message: 'Check your messages and notifications from testers here.',
      position: 'left'
    },
    {
      selector: '#discover-section',
      title: 'Discover',
      message: 'Explore other projects and see what others are working on!',
      position: 'top'
    }
  ],

  addGameDev: [
    {
      selector: '#addgame-content',
      title: 'Create Your Game',
      message: 'This page lets you publish a new game project for testers.',
      position: 'right'
    },
    {
      selector: '#addgame-pic',
      title: 'Game Icon',
      message: 'Upload an icon for your game. Testers will see this in Discover.',
      position: 'right'
    },
    {
      selector: '#addgame-name',
      title: 'Game Name',
      message: 'Enter your game\'s title.',
      position: 'right'
    },
    {
      selector: '#addgame-company',
      title: 'Studio Name',
      message: 'Your studio or team name will show up to testers.',
      position: 'right'
    },
    {
      selector: '.addgame-assign-ctn',
      title: 'Recruitment Deadline',
      message: 'Set when testers can no longer apply.',
      position: 'top'
    },
    {
      selector: '.addgame-biginput',
      title: 'Description',
      message: 'Describe your game clearly to attract better testers.',
      position: 'right'
    },
    {
      selector: '.contact-content',
      title: 'QA Contact',
      message: 'Testers can contact this person for issues or questions.',
      position: 'left'
    },
    {
      selector: '.survey-btn',
      title: 'Publish',
      message: 'Publish your game when everything is ready.',
      position: 'top'
    }
  ],

  // Tester onboarding for the Discover page
  discover: [
    {
      selector: '.header-card',
      title: 'Welcome to Discover!',
      message: 'This is your hub for finding and joining exciting game testing projects. Let\'s take a quick tour!',
      position: 'right'
    },
    {
      selector: '#search-bar-step',
      title: 'Search Projects',
      message: 'Use this search bar to quickly find projects by name.',
      position: 'bottom'
    },
    {
      selector: '#tags-dropdown-step',
      title: 'Filter by Tags',
      message: 'Click here to filter projects by categories like Mechanics, Usability, Design, and more!',
      position: 'bottom'
    },
    {
      selector: '#view-toggles-step',
      title: 'View Options',
      message: 'Switch between grid and list views, or sort projects to customize your browsing experience.',
      position: 'left'
    },
    {
      selector: '#onboarding-projects-grid',
      title: 'Browse Projects',
      message: 'All available projects appear here. Scroll through the list to find your next playtest!',
      position: 'left',
      disableScroll: true
    }
  ],

  // Achievements page onboarding (for all users - testers, devs, and admins)
  achievements: [
    {
      selector: '.title-header',
      title: 'Welcome to Achievements! 🏆',
      message: 'This is where you can track your progress and celebrate your accomplishments as you test games!',
      position: 'bottom'
    },
    {
      selector: '.toggle-achievements',
      title: 'Toggle View',
      message: 'Switch between viewing completed achievements and ones you\'re still working on. Try clicking these buttons!',
      position: 'bottom'
    },
    {
      selector: '.achievements-content',
      title: 'Your Achievements',
      message: 'All your achievements are displayed here. Each card shows your progress or completion status.',
      position: 'top'
    },
    {
      selector: '.achievement-card:first-child',
      title: 'Achievement Cards',
      message: 'Each card displays an achievement\'s title, description, and your current progress. Keep testing to unlock more!',
      position: 'right'
    },
    {
      selector: '.progress-container',
      title: 'Track Your Progress',
      message: 'The progress bar shows how close you are to completing an achievement. Keep going!',
      position: 'left'
    }
  ],

  projectDetailDev: [
    {
      selector: '#gamedetail-sidebar',
      title: 'Sidebar Navigation',
      message: 'Use the sidebar to navigate between different sections of your project.',
      position: 'left'
    },
    {
      selector: '.main-content',
      title: 'Content Area',
      message: 'The main content area changes based on which sidebar option you click.',
      position: 'top'
    }
  ],

  testerListDev: [
    {
      selector: '#title-wrapper',
      title: 'Project Overview',
      message: 'Here is the project summary including the name, company, image, and end time.',
      position: 'bottom'
    },
    {
      selector: '.tester-list',
      title: 'Tester List',
      message: 'Here is the list of testers that have joined this project.',
      position: 'left'
    },
    {
      selector: '.add-new-tester',
      title: 'Add New Tester',
      message: 'Click here to add new testers to your project.',
      position: 'left'
    }
  ],

  surveyDev: [
    {
      selector: '.gda-survey-box',
      title: 'Survey Overview',
      message: 'This section contains all surveys you have created for this project.',
      position: 'right'
    },
    {
      selector: '.survey-btn-add',
      title: 'Add New Survey',
      message: 'Click here to create a new survey for your playtesters.',
      position: 'right'
    },
    {
      selector: '.gda-survey-list',
      title: 'Survey List',
      message: 'Below is the list of all surveys. You can see the status, edit, publish, or view results.',
      position: 'right'
    },
    {
      selector: '.gda-text-container',
      title: 'Survey Status',
      message: 'Survey names and status labels (e.g. New, Pending Approval, Approved, Archived) appear here.',
      position: 'right'
    },
    {
      selector: '.button-container',
      title: 'Survey Actions',
      message: 'Each survey includes actions such as edit, publish, view tester list, details, and results.',
      position: 'left'
    }
  ],

  searchSurveyDev: [
    {
      selector: '#header-card',
      title: 'Search & Filters',
      message: 'Search surveys by name, status, and tags to quickly find what you need.',
      position: 'right'
    },
    {
      selector: '#dropdown-2',
      title: 'Filter by Status',
      message: 'Filter surveys based on approval or publish status.',
      position: 'bottom'
    },
    {
      selector: '#dropdown-1',
      title: 'Filter by Tags',
      message: 'Filter surveys by design tags such as UI, Mechanics, Performance, etc.',
      position: 'bottom'
    },
    {
      selector: '#gridview',
      title: 'Grid View',
      message: 'Switch to a grid view layout.',
      position: 'left'
    },
    {
      selector: '#listview',
      title: 'List View',
      message: 'Switch to a list view layout.',
      position: 'left'
    }
  ],

  recordingsDev: [
    {
      selector: '.gda-recordings-box',
      title: 'Gameplay Overview',
      message: 'This section contains all play sessions and recordings for this project.',
      position: 'right'
    },
    {
      selector: '.recording-btn-add',
      title: 'Add New Playsession',
      message: 'Create a new play session for testers to record gameplay.',
      position: 'right'
    },
    {
      selector: '.gda-recordings-list',
      title: 'Playsession List',
      message: 'All play sessions are listed here with their current status.',
      position: 'right'
    },
    {
      selector: '.gda-text-container',
      title: 'Session Status',
      message: 'Each session shows its name, progress state, and result count.',
      position: 'right'
    },
    {
      selector: '.button-container',
      title: 'Session Actions',
      message: 'Manage each session: add testers, view details, publish, or see results.',
      position: 'left'
    }
  ],

  // Achievements page - completed view (shown only after toggling)
  achievementsCompleted: [
    {
      selector: '.completion-badge',
      title: 'Completed Achievements 🎉',
      message: 'Great work! These badges show the level of achievements you\'ve completed. Keep testing to earn more!',
      position: 'left'
    }
  ],

  // ── Playtest onboarding (Step 1: Select Template / Info) ──────────────────
  playtestInfo: [
    {
      selector: '#playtest-name',
      title: 'Playtest Name',
      message: 'This is where you find the name of each of your playtests.',
      position: 'bottom'
    },
    {
      selector: '#playtest-period',
      title: 'Playtest Period',
      message: 'Playtest Period is where you fill in the dates of when you plan to start and finish the playtest.',
      position: 'bottom'
    },
    {
      selector: '#player-information',
      title: 'Player Information',
      message: 'Player Information is a brief description of the tasks you will be asked to perform.',
      position: 'right'
    },
    {
      selector: '#other-information',
      title: 'Other Information',
      message: 'Other Information contains other information and questions that the developer has for you.',
      position: 'right'
    },
    {
      selector: '.btn-save',
      title: 'Save Progress',
      message: 'The Save Progress button will save all of the information you have already filled out. The playtest can be accessed, finished, and edited at a later date.',
      position: 'top'
    }
  ],

  // ── Playtest onboarding (Step 2: Audio + Video check) ────────────────────
  playtestAVCheck: [
    {
      selector: '.section-card:first-child',
      title: 'Screen Share Preview',
      message: 'This panel shows a live preview of the screen you\'ve chosen to share during your playtest session.',
      position: 'right'
    },
    {
      selector: '.webcam-interface',
      title: 'Webcam Preview',
      message: 'Enable your webcam here if you\'d like to include a face-cam alongside your gameplay recording.',
      position: 'left'
    },
    {
      selector: '.config-grid',
      title: 'Device Configuration',
      message: 'Use these controls to test your microphone and select the screen or window you want to record.',
      position: 'top'
    },
    {
      selector: '.btn-primary',
      title: 'Ready to Record',
      message: 'Once your devices are set up, click here to move on and start your gameplay recording.',
      position: 'top'
    }
  ],

  // ── Playtest onboarding (Step 3: Recording + Notes) ──────────────────────
  playtestMain: [
    {
      selector: '.recording-card',
      title: 'Screen Recording',
      message: 'This is your recording panel. Start a new recording when you\'re ready to begin your playtest session.',
      position: 'right'
    },
    {
      selector: '.recording-actions',
      title: 'Recording Controls',
      message: 'Use "Start New Recording" to begin and "Stop & Save" to finish. Make sure to stop the recording before submitting.',
      position: 'top'
    },
    {
      selector: '.timeline-container',
      title: 'Timeline',
      message: 'After recording, use the timeline scrubber to review your session and drag notes onto specific timestamps.',
      position: 'top'
    },
    {
      selector: '.drag-drop-card',
      title: 'Session Notes',
      message: 'This panel holds your instructions and bug reports. Drag items onto the timeline to mark exactly when they occurred.',
      position: 'left'
    },
    {
      selector: '.tab-bar',
      title: 'Instructions & Bugs',
      message: 'Switch between the Instructions, Bugs, and Webcam tabs to manage your session notes and review your webcam recording.',
      position: 'bottom'
    }
  ],
};

/**
 * Initialize onboarding for a specific page
 * @param {string} pageName - The name of the page
 * @param {number} userType - The type of user (0 = tester, 1 = developer, 2 = admin)
 * @param {Object} onboardingService - The onboarding service instance
 * @param {Object} store - The Vuex store instance
 * @param {boolean} force - Force-show even if recently seen (used by help button)
 */
export function initializePageOnboarding(pageName, userType, onboardingService, store, force = false) {
  console.log("🟡 initializePageOnboarding:", pageName, userType);

  onboardingService.init(store);

  let configKey = null;

  if (pageName === 'projects' && userType === 1) {
    configKey = 'projectsDev';
  } else if (pageName === 'discover' && userType === 0) {
    configKey = 'discover';
  } else if (pageName === 'achievements') {
    configKey = 'achievements';
  } else if (pageName === 'addGame' && userType === 1) {
    configKey = 'addGameDev';
  } else if (pageName === 'projectDetail' && userType === 1) {
    configKey = 'projectDetailDev';
  } else if (pageName === 'testerList' && userType === 1) {
    configKey = 'testerListDev';
  } else if (pageName === 'survey' && userType === 1) {
    configKey = 'surveyDev';
  } else if (pageName === 'recordings' && userType === 1) {
    configKey = 'recordingsDev';
  } else if (pageName === 'searchSurvey' && userType === 1) {
    configKey = 'searchSurveyDev';
  }
  // Playtest pages (available to testers, userType === 0)
  else if (pageName === 'playtestInfo') {
    configKey = 'playtestInfo';
  } else if (pageName === 'playtestAVCheck') {
    configKey = 'playtestAVCheck';
  } else if (pageName === 'playtestMain') {
    configKey = 'playtestMain';
  }

  if (configKey && onboardingConfigs[configKey]) {
    const userId = localStorage.getItem('userId');
    onboardingService.registerPageOnboarding(configKey, onboardingConfigs[configKey]);
    onboardingService.startPageOnboarding(configKey, userId, force);
  }
}

export default onboardingConfigs;


// WEBPACK FOOTER //
// ./src/services/onboardingConfigs.js